import { useLocation } from '@gatsbyjs/reach-router';
import { Container } from '@mui/material';
import { navigate, type PageProps } from 'gatsby';
import React, { useEffect, useMemo } from 'react';
import { type FallbackProps, withErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { DrilldownHeader } from '@components/DrilldownHeader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { RoleGuard } from '@components/Guards';

import { RoleName } from '@constants/role_name';
import * as ROUTES from '@constants/routes';

import { ScanResultProvider } from '@contexts/index';

import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import AppLayout from '@layouts/AppLayout';

import AllergiesDrilldown from '@views/Records/Allergies/Drilldown';
import AppointmentsDrilldown from '@views/Records/Appointments/Drilldown';
import EmergencyVisitsDrilldown from '@views/Records/EmergencyVisits/Drilldown';
import FamilyHistoryDrilldown from '@views/Records/FamilyHistory/Drilldown';
import GeneticInformationDrilldown from '@views/Records/GeneticInformation/Drilldown';
import HospitalizationsDrilldown from '@views/Records/Hospitalizations/Drilldown';
import ImagingTestsDrilldown from '@views/Records/ImagingTests/Drilldown';
import ImmunizationsDrilldown from '@views/Records/Immunizations/Drilldown';
import InsuranceDrilldown from '@views/Records/Insurance/Drilldown';
import LabResultsDrilldown from '@views/Records/LabResults/SingleLabDrilldown';
import MedicalConditionsDrilldown from '@views/Records/MedicalConditions/Drilldown';
import MedicationsDrilldown from '@views/Records/Medications/Drilldown';
import ScanResultsDrilldown from '@views/Records/Scan/Drilldown/AuthDrilldown';
import SurgeriesDrilldown from '@views/Records/Surgeries/Drilldown';

enum RecordsDrilldownTypeParam {
  Allergies = 'allergies',
  Appointments = 'appointments',
  EmergencyVisits = 'emergency-visits',
  FamilyHistory = 'family-history',
  GeneticInformation = 'genetic-information',
  Hospitalizations = 'hospitalizations',
  ImagingTests = 'imaging-tests',
  Immunizations = 'immunizations',
  Insurance = 'insurance',
  LabResults = 'lab-results',
  MedicalConditions = 'medical-conditions',
  Medications = 'medications',
  Surgeries = 'surgeries',
  VitalSigns = 'vital-signs',
  Body = 'body',
  CoughScans = 'cough-scan-history',
}

enum ClientAllowedRecordTypes {
  Appointments = RecordsDrilldownTypeParam.Appointments,
  FamilyHistory = RecordsDrilldownTypeParam.FamilyHistory,
  GeneticInformation = RecordsDrilldownTypeParam.GeneticInformation,
  Immunizations = RecordsDrilldownTypeParam.Immunizations,
  Medications = RecordsDrilldownTypeParam.Medications,
}

function RecordsDrilldownPage({ params }: PageProps) {
  const { startAddingRecord, stopCurating } = useCuration();
  const {
    state: { isClient, isCurator, isPractitioner },
  } = useSession();
  const isAdding = params.id === 'add';

  useEffect(() => {
    const clientAddRecord =
      isClient &&
      Object.values<string>(ClientAllowedRecordTypes).includes(params.type);

    if (isAdding && (isCurator || isPractitioner || clientAddRecord)) {
      startAddingRecord();
    }

    return () => stopCurating();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdding, params.type]);

  useEffect(() => {
    const { id, type } = params;
    if (
      !type ||
      !id ||
      !Object.values<string>(RecordsDrilldownTypeParam).includes(type)
    ) {
      navigate(ROUTES.DASHBOARD);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const children: React.ReactNode = useMemo(() => {
    switch (params.type) {
      case RecordsDrilldownTypeParam.Allergies:
        return <AllergiesDrilldown {...params} />;
      case RecordsDrilldownTypeParam.Appointments:
        return <AppointmentsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.CoughScans:
        return (
          <ScanResultProvider scanType={params.type}>
            <ScanResultsDrilldown {...params} />
          </ScanResultProvider>
        );
      case RecordsDrilldownTypeParam.EmergencyVisits:
        return <EmergencyVisitsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.FamilyHistory:
        return <FamilyHistoryDrilldown {...params} />;
      case RecordsDrilldownTypeParam.GeneticInformation:
        return <GeneticInformationDrilldown {...params} />;
      case RecordsDrilldownTypeParam.Hospitalizations:
        return <HospitalizationsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.Insurance:
        return <InsuranceDrilldown {...params} />;
      case RecordsDrilldownTypeParam.ImagingTests:
        return <ImagingTestsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.Immunizations:
        return <ImmunizationsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.LabResults:
        return <LabResultsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.MedicalConditions:
        return <MedicalConditionsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.Medications:
        return <MedicationsDrilldown {...params} />;
      case RecordsDrilldownTypeParam.Surgeries:
        return <SurgeriesDrilldown {...params} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.type, params.id]);

  if (
    isAdding &&
    !Object.values<string>(ClientAllowedRecordTypes).includes(params.type) &&
    children
  ) {
    return (
      <RoleGuard roles={[RoleName.Curator, RoleName.Practitioner]}>
        {children}
      </RoleGuard>
    );
  }

  return children;
}

const Fallback = (props: FallbackProps) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();

  const getTitle = () => {
    if (pathname.includes(RecordsDrilldownTypeParam.Allergies)) {
      return 'allergy';
    } else if (pathname.includes(RecordsDrilldownTypeParam.Appointments)) {
      return 'appointment';
    } else if (pathname.includes(RecordsDrilldownTypeParam.CoughScans)) {
      return 'coughScan';
    } else if (pathname.includes(RecordsDrilldownTypeParam.EmergencyVisits)) {
      return 'emergency';
    } else if (pathname.includes(RecordsDrilldownTypeParam.FamilyHistory)) {
      return 'familyHistory';
    } else if (
      pathname.includes(RecordsDrilldownTypeParam.GeneticInformation)
    ) {
      return 'geneticInfo';
    } else if (pathname.includes(RecordsDrilldownTypeParam.Hospitalizations)) {
      return 'hospitalization';
    } else if (pathname.includes(RecordsDrilldownTypeParam.ImagingTests)) {
      return 'imaging';
    } else if (pathname.includes(RecordsDrilldownTypeParam.Immunizations)) {
      return 'immunization';
    } else if (pathname.includes(RecordsDrilldownTypeParam.Insurance)) {
      return 'insurance';
    } else if (pathname.includes(RecordsDrilldownTypeParam.LabResults)) {
      return 'labResult';
    } else if (pathname.includes(RecordsDrilldownTypeParam.MedicalConditions)) {
      return 'condition';
    } else if (pathname.includes(RecordsDrilldownTypeParam.Medications)) {
      return 'medication';
    } else if (pathname.includes(RecordsDrilldownTypeParam.Surgeries)) {
      return 'surgery';
    }
  };

  const title = formatMessage({
    id: `fallbackDrilldown.${getTitle()}` || 'fallbackDrilldown.record',
  });

  return (
    <AppLayout friendlyName={title}>
      <DrilldownHeader onBackClick={() => navigate(-1)} title={title} />
      <Container maxWidth="md">
        <ErrorBoundaryFallback {...props} />
      </Container>
    </AppLayout>
  );
};

export default withErrorBoundary(RecordsDrilldownPage, {
  FallbackComponent: (props) => <Fallback {...props} />,
});
